import type { DesignBody } from '@repo/api-contracts';
import { type ReactElement, useCallback, useMemo } from 'react';
import { useMyQuoteDesignViewQuery } from '@/entities/quote';
import { useModalDialog } from '@/shared/lib';
import { Button, DesignViewer, DialogFrame } from '@/shared/ui';

export interface QuoteDesignViewDialogProps {
  readonly quoteRequestId: string;
  /** 견적 상세가 준 사본 본문(design.designBody) — 뷰 응답에는 본문이 없다(뷰어 설계 V3). */
  readonly designBody: DesignBody;
  readonly onClose: () => void;
}

/**
 * 데스크톱은 화면 가까이 큰 창(최대 72rem·위아래 2rem 여백), 휴대폰(sm 미만)은 화면을 꽉 채운다(V2) — 맞춤 그림이 상세의 썸네일보다 커야 한다.
 * 열렸을 때만 세로 flex 다: 제목·버튼 줄은 제 높이, 본문이 남는 높이를 다 쓴다(닫힌 <dialog> 의 기본 display:none 을 덮지 않는다).
 */
const DIALOG_CLASS =
  'open:flex open:flex-col max-w-6xl sm:h-[calc(100dvh-4rem)] sm:w-[calc(100%-4rem)] max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none';
/** 본문은 높이 상한(70dvh)을 창 높이(max-h-full)로 풀고 남는 높이를 채운다 — 뷰어가 그 안에서 늘어난다. 아주 낮은 창에서는 스크롤한다(DialogFrame 기본). */
const BODY_CLASS = 'flex min-h-0 max-h-full flex-1 flex-col';

/**
 * 견적 디자인 보기 창(뷰어 설계 V2·V3·V12·V13) — 견적에 고정된 사본을 모든 면에서 확대해 본다. 편집은 없다.
 * 열려 있는 동안만 마운트된다. 그동안 뷰(고정 편집영역 정의·폰트·잉크·이미지 서명 주소)를 조회하고 주소를 주기적으로 다시 받는다.
 * 닫으면(언마운트) 조회를 멈추고 여는 버튼으로 포커스를 돌려준다. 모양은 공용 대화상자 틀(DialogFrame)이다.
 */
export const QuoteDesignViewDialog = ({
  quoteRequestId,
  designBody,
  onClose,
}: QuoteDesignViewDialogProps): ReactElement => {
  const modal = useModalDialog(onClose, true);
  const {
    data: view,
    isError,
    isFetching,
    errorUpdateCount,
    refetch,
  } = useMyQuoteDesignViewQuery(quoteRequestId, { enabled: true });
  // 뷰 없이 한 번이라도 실패했으면 실패 안내를 둔다. 데이터 없이 다시 받으면 쿼리는 오류를 지우고 pending 으로 돌아가므로
  // (TanStack Query v5) isError 만 보면 다시 시도하는 동안 안내가 '불러오는 중'으로 바뀐다 — 실패 횟수로 안내를 지킨다.
  const failed = view === undefined && (isError || errorUpdateCount > 0);
  const assetUrls = useMemo(
    () =>
      new Map<string, string>(
        view?.status === 'READY' ? view.assets.map((asset) => [asset.id, asset.url] as const) : [],
      ),
    [view],
  );
  // 만료(403)된 주소는 뷰를 한 번 다시 받아 새 서명 주소로 바꾼다 — 뷰어가 그 면을 다시 그린다(V12).
  // refetch() 는 쿼리 캐시를 새 뷰로 바꾼 뒤에 끝나므로, 이 함수가 끝나면 곧 이어지는 렌더에서 새 주소가 뷰어에 들어간다.
  const refreshUrls = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  return (
    <DialogFrame
      modal={modal}
      title="디자인 보기"
      className={DIALOG_CLASS}
      bodyClassName={BODY_CLASS}
      footer={
        <Button type="button" variant="outline" className="max-sm:h-11" onClick={onClose}>
          닫기
        </Button>
      }
    >
      {view === undefined && !failed ? (
        <p role="status" className="m-auto py-16 text-center text-muted-foreground">
          디자인을 불러오는 중…
        </p>
      ) : null}
      {failed ? (
        <div role="alert" className="m-auto flex flex-col items-center gap-4 py-16 text-center">
          <p className="text-destructive">디자인을 불러오지 못했어요</p>
          {/* 다시 받는 동안에는 잠가 겹쳐 누르지 못하게 한다. 휴대폰에서는 44px. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="max-sm:h-11"
            isLoading={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            {isFetching ? '다시 불러오는 중…' : '다시 시도'}
          </Button>
        </div>
      ) : null}
      {view?.status === 'UNSUPPORTED' ? (
        <p className="m-auto py-16 text-center text-muted-foreground">
          이 견적의 디자인은 지금 보기에서 열 수 없어요
        </p>
      ) : null}
      {view?.status === 'READY' ? (
        <DesignViewer
          definition={view.definition}
          colorOptionId={view.colorOptionId}
          body={designBody}
          assetUrls={assetUrls}
          fonts={view.fonts}
          inks={view.inkColors}
          refreshUrls={refreshUrls}
          className="min-h-0 flex-1"
        />
      ) : null}
    </DialogFrame>
  );
};
